import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import NestedItemSelect, {
  Section,
} from "@components/NestedItemSelect/NestedItemSelect";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useParentLines } from "../api/useParentLines";
import { normalize, toSourceItems } from "../api/lineSelection";
import type { SourceItem } from "../types";

export interface LineSelectProps {
  projectId: string;
  onChange: (items: SourceItem[]) => void;
}

// Picks the parent project's lines to copy into the change order. The
// sections are seeded once from the parent lines and then owned here, so a
// refetch doesn't wipe the selection.
export const LineSelect = ({ projectId, onChange }: LineSelectProps) => {
  const {
    sections: parentSections,
    isLoading,
    error,
  } = useParentLines(projectId);
  const [sections, setSections] = useState<Section[]>([]);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    // Don't seed from a failed load: that would show an empty picker as if the
    // project had no services.
    if (!isLoading && !error && !seeded) {
      setSeeded(true);
      setSections(parentSections);
    }
  }, [isLoading, error, seeded, parentSections]);

  const handleChange = (next: Section[]) => {
    const normalized = normalize(sections, next);
    setSections(normalized);
    onChange(toSourceItems(normalized));
  };

  if (!seeded && !isLoading && error) {
    return (
      <Form.Text className="text-danger" role="alert">
        Couldn&apos;t load this project&apos;s services. You can still create
        the change request and add services afterwards.
      </Form.Text>
    );
  }
  if (!seeded) return <ScopeStackSpinner />;

  return (
    <div className="changeOrderLineSelect">
      <Form.Text className="text-muted mb-2">
        Leave empty to add only new services.
      </Form.Text>
      <NestedItemSelect
        title="LINES TO ADJUST"
        sections={sections}
        onSectionsChange={handleChange}
      />
      <Form.Text className="text-muted">
        Selecting a subservice brings its service along.
      </Form.Text>
    </div>
  );
};

export default LineSelect;
